import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ThumbsUp, ThumbsDown, MessageSquare, Save } from 'lucide-react';
import translationLabAPI from '../../services/translationLabService';

// Inline 👍/👎 + comment control for one result of one run.
//
// Modelled on translation_chat/TranslationFeedbackControl (same rule: a thumbs-down needs a
// comment), with one difference — lab ratings are persisted, so this control starts from whatever
// rating the result already carries instead of always starting blank.
const TranslationLabFeedbackControl = ({ result, onSaved }) => {
  const existing = result.feedback;
  const [rating, setRating] = useState(existing ? existing.rating : null);
  const [comment, setComment] = useState(existing?.comment || '');
  const [showComment, setShowComment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(Boolean(existing));
  const [error, setError] = useState('');

  // A second click while the first request is still open would race it: the two responses can land
  // in either order, so the older verdict could end up as the stored one.
  const submit = async (nextRating, nextComment) => {
    if (saving) return;
    if (nextRating === false && !(nextComment || '').trim()) {
      setShowComment(true);
      setError('Comment required for a thumbs down rating');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data } = await translationLabAPI.saveFeedback(result.id, {
        rating: nextRating,
        comment: nextComment || '',
      });
      setSaved(true);
      setShowComment(false);
      if (onSaved) onSaved(result.id, data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save feedback');
    } finally {
      setSaving(false);
    }
  };

  const handleThumb = (value) => {
    if (saving) return;
    setRating(value);
    setSaved(false);
    setError('');
    if (value === false) {
      setShowComment(true);
    } else {
      submit(value, comment);
    }
  };

  return (
    <div className="mt-1.5">
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={saving}
          onClick={() => handleThumb(true)}
          className={`p-1 rounded transition-colors disabled:opacity-50 ${rating === true ? 'text-brand-green' : 'text-ink-mute hover:text-brand-green'}`}
          aria-label="Good translation"
        >
          <ThumbsUp className="h-3 w-3" />
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => handleThumb(false)}
          className={`p-1 rounded transition-colors disabled:opacity-50 ${rating === false ? 'text-negative' : 'text-ink-mute hover:text-negative'}`}
          aria-label="Bad translation"
        >
          <ThumbsDown className="h-3 w-3" />
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => setShowComment((v) => !v)}
          className={`p-1 rounded transition-colors disabled:opacity-50 ${comment ? 'text-ink-dim' : 'text-ink-mute'} hover:text-ink`}
          aria-label="Comment"
        >
          <MessageSquare className="h-3 w-3" />
        </button>
        {saved && !showComment && <span className="text-[10px] text-ink-mute">saved</span>}
      </div>

      {showComment && (
        <div className="mt-1.5">
          <Textarea
            rows={2}
            className="text-xs min-h-0"
            placeholder={rating === false ? 'Comment (required)' : 'Comment (optional)'}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {error && <p className="text-[10px] text-negative mt-1">{error}</p>}
          <div className="flex justify-end gap-1 mt-1">
            <Button size="sm" variant="ghost" onClick={() => setShowComment(false)}>Close</Button>
            <Button size="sm" disabled={rating === null || saving} onClick={() => submit(rating, comment)} className="gap-1">
              <Save className="h-3 w-3" />Save
            </Button>
          </div>
        </div>
      )}

      {!showComment && comment && (
        <p className="text-[10px] text-ink-mute mt-0.5 line-clamp-2">{comment}</p>
      )}
    </div>
  );
};

export default TranslationLabFeedbackControl;
